import React, { useEffect } from "react";
import { CalendarEvent } from "../models/CalendarEvent";
import PlotAPI from "../plotAPI";
import UserInfo from "../userInfo";
import CalendarEventsFilterInfo from "./calendarEventsFilterInfo";

const PAGE_SIZE = 300;

interface Props {
    userInfo: UserInfo | null;
    projects: any[];
    projectInfo: (projectId: string | number) => any | null;
    overrideTimezone: string | null;
}

const UseCalendarEvents = (props: Props) => {
    const [calendarEventsFilterInfo, setCalendarEventsFilterInfo] = React.useState<CalendarEventsFilterInfo>(
        new CalendarEventsFilterInfo()
    );
    const [calendarEventsByProject, setCalendarEventsByProject] = React.useState<
        { unique_token: string; calendarEvents: CalendarEvent[] }[]
    >([]);

    const refreshCalendarEventsForProject = (
        project: any
    ): Promise<{
        unique_token: string;
        calendarEvents: CalendarEvent[];
    }> => {
        const filterInfo = Object.create(calendarEventsFilterInfo) as CalendarEventsFilterInfo;

        filterInfo.page = 1;
        filterInfo.pageSize = PAGE_SIZE;
        const calendarEvents: CalendarEvent[] = [];

        const fetchPage = (): Promise<void> => {
            return PlotAPI.getCalendarEvents(
                project.claimed_by_team_subdomain || "projects",
                project.unique_token,
                filterInfo,
                props.overrideTimezone || project.timezone
            ).then((response) => {
                calendarEvents.push(...response.calendar_events);

                if (response.calendar_events.length === 0 || calendarEvents.length >= response.total_count) {
                    return;
                }

                filterInfo.page += 1;
                return fetchPage();
            });
        };

        return fetchPage().then(() => {
            return { unique_token: project.unique_token, calendarEvents };
        });
    };

    const refreshCalendarEventsByProject = (): Promise<void> => {
        // Don't allow public users to try to get a bunch of calendar events
        if (!props.userInfo) {
            setCalendarEventsByProject([]);
            return Promise.resolve();
        }

        // Our projectIds filter is an array of ids to filter by. If it's empty, we query all projects.
        // Because of this, we have to have some special logic on the query side
        let projectsToQuery = props.projects;
        if (calendarEventsFilterInfo.projectIds.length > 0) {
            projectsToQuery = props.projects.filter((project) =>
                calendarEventsFilterInfo.projectIds.includes(project.id)
            );
        }

        return Promise.all(projectsToQuery.map((project) => refreshCalendarEventsForProject(project))).then(
            (responses) => {
                setCalendarEventsByProject(responses);
            }
        );
    };

    useEffect(() => {
        refreshCalendarEventsByProject();
    }, [props.projects, calendarEventsFilterInfo]);

    const calendarEventsForProject = (projectId: string | number): CalendarEvent[] => {
        const projectToken = props.projectInfo(projectId)?.unique_token;

        return calendarEventsByProject.find((p) => p.unique_token === projectToken)?.calendarEvents || [];
    };

    const updateCalendarEventInState = (projectToken: string, calendarEvent: CalendarEvent) => {
        const calendarEventsByProjectCopy = [...calendarEventsByProject];

        const projectCalendarEventsIndex = calendarEventsByProjectCopy.findIndex(
            (project) => project.unique_token === projectToken
        );

        if (projectCalendarEventsIndex === -1) {
            return;
        }

        const calendarEventIndex = calendarEventsByProjectCopy[projectCalendarEventsIndex].calendarEvents.findIndex(
            (ce) => ce.id === calendarEvent.id
        );

        // Upsert the calendar event into the list
        if (calendarEventIndex === -1) {
            calendarEventsByProjectCopy[projectCalendarEventsIndex].calendarEvents.push(calendarEvent);
        } else {
            calendarEventsByProjectCopy[projectCalendarEventsIndex].calendarEvents[calendarEventIndex] = calendarEvent;
        }

        setCalendarEventsByProject(calendarEventsByProjectCopy);
    };

    const createCalendarEvent = (newCalendarEvent: CalendarEvent): Promise<CalendarEvent | null> => {
        if (!newCalendarEvent.projectId) {
            return Promise.resolve(null);
        }

        const project = props.projectInfo(newCalendarEvent.projectId);

        if (!project) {
            return Promise.resolve(null);
        }

        return PlotAPI.createCalendarEvent(
            project.claimed_by_team_subdomain || "projects",
            project.unique_token,
            newCalendarEvent,
            project.timezone
        );
    };

    const updateCalendarEvent = (updatedCalendarEvent: CalendarEvent) => {
        if (!updatedCalendarEvent.projectId) {
            return Promise.resolve(null);
        }

        const project = props.projectInfo(updatedCalendarEvent.projectId);

        if (!project) {
            return Promise.resolve(null);
        }

        return PlotAPI.updateCalendarEvent(
            project.claimed_by_team_subdomain || "projects",
            project.unique_token,
            updatedCalendarEvent,
            project.timezone
        );
    };

    const deleteCalendarEvent = (calendarEventToDelete: CalendarEvent) => {
        if (!calendarEventToDelete.projectId) {
            return Promise.resolve(false);
        }

        const project = props.projectInfo(calendarEventToDelete.projectId);

        if (!project) {
            return Promise.resolve(false);
        }

        return PlotAPI.deleteCalendarEvent(
            project.claimed_by_team_subdomain || "projects",
            project.unique_token,
            calendarEventToDelete
        );
    };

    return {
        calendarEventsFilterInfo,
        setCalendarEventsFilterInfo,
        refreshCalendarEventsByProject,
        calendarEventsByProject,
        calendarEventsForProject,
        updateCalendarEventInState,
        createCalendarEvent,
        updateCalendarEvent,
        deleteCalendarEvent,
    };
};

export default UseCalendarEvents;
